import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, BadgeCheckIcon, CheckCircle2Icon, ShieldCheckIcon, SunIcon, UsersIcon } from 'lucide-react';
import { ExitIntentModal } from './ExitIntentModal';
import { useExitIntent } from '../hooks/useExitIntent';
const BACKGROUND_IMAGE = 'https://cdn.magicpatterns.com/uploads/dRkvsWLnbieL2fQb4aMYZn/pexels-lena-netkach-395486-38041368.jpg';

/** Replace with your affiliate offer URL. */
const OFFER_URL = 'https://trackrw.dailyhomewellness.com/click';
const BENEFITS = ['Free solar quotes in 24–48 hours', 'Licensed & insured local installers', 'No obligation — compare offers'];
type Answer = 'yes' | 'no' | null;
export function BridgePage() {
  const [answer, setAnswer] = useState<Answer>(null);
  const {
    triggered,
    reset
  } = useExitIntent({
    enabled: answer !== 'yes'
  });
  const goToOffer = () => {
    window.location.href = OFFER_URL;
  };
  return <main className="relative min-h-screen w-full overflow-hidden bg-bark-950 min-h-[100dvh]" data-id="element-1" data-mp="1p9z2b8zyi09h">
      <img src={BACKGROUND_IMAGE} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover brightness-125 contrast-110 saturate-110" data-id="element-2" data-mp="3eah7rs5lspvi" />
      <div className="absolute inset-0 bg-bark-950/45 sm:bg-bark-950/40" aria-hidden="true" data-id="element-3" data-mp="3o8esi5o984jd" />

      <div className="relative mx-auto flex min-h-screen min-h-[100dvh] w-full max-w-2xl flex-col px-5 pt-7 pb-[max(1.75rem,env(safe-area-inset-bottom))] sm:px-8 sm:pt-12 sm:pb-12 lg:max-w-3xl" data-id="element-4" data-mp="11m8gu7y2upl">
        <header className="flex items-center gap-2.5" data-id="element-5" data-mp="arfihqsfv1rq">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-ember-500" data-id="element-6" data-mp="arxpnign30ef">
            <SunIcon className="h-5 w-5 text-white" data-id="element-7" data-mp="fp7ditodr405" />
          </span>
          <span className="text-sm font-bold uppercase tracking-[0.2em] text-white" data-id="element-8" data-mp="jsm0y5enemnm">
            Solar Savings
          </span>
        </header>

        <div className="flex flex-1 flex-col justify-center py-8 sm:py-14" data-id="element-9" data-mp="orgiz54sk76o">
          <AnimatePresence mode="wait" data-id="element-10" data-mp="2kv4t43pdlfvs">
            {answer === null && <motion.section key="question" initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -12
          }} transition={{
            duration: 0.3,
            ease: 'easeOut'
          }} data-id="element-11" data-mp="2gtj97yefc1c3">
                <p className="max-w-xl font-serif text-lg leading-relaxed text-white [text-shadow:0_2px_16px_rgba(8,6,4,0.9)] sm:text-xl" data-id="element-12" data-mp="34i2j2id4gm6a">
                  Homeowners matched through this program have reported over{' '}
                  <strong className="font-semibold text-ember-400" data-id="element-13" data-mp="3dj92p0b2id3r">
                    $6.7M in electric bill savings
                  </strong>{' '}
                  in 2026.
                </p>

                <span className="mt-5 inline-flex items-center gap-2 rounded-full border border-ember-500/40 bg-ember-500/10 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-ember-400 sm:text-xs" data-id="element-14" data-mp="1j46hvfwyrj76">
                  <BadgeCheckIcon className="h-3.5 w-3.5" data-id="element-15" data-mp="3pujhbvibr29c" />
                  Homeowner Energy Survey
                </span>

                <h1 className="mt-4 font-serif text-[1.75rem] font-bold leading-[1.12] text-white [text-shadow:0_2px_20px_rgba(8,6,4,0.85)] xs:text-[2rem] sm:text-5xl lg:text-[3.5rem]" data-id="element-16" data-mp="3uufno1ehv5s2">
                  Do you pay more than{' '}
                  <span className="text-ember-400" data-id="element-17" data-mp="3730htynie6c2">$99 a month</span> on your
                  electric bill?
                </h1>

                <div className="mt-7 grid gap-3 sm:mt-9 sm:grid-cols-2 sm:gap-4" data-id="element-18" data-mp="3c2v6dvjtajty">
                  <button type="button" onClick={() => {
                setAnswer('yes');
                goToOffer();
              }} className="group flex min-h-[60px] w-full touch-manipulation items-center justify-center gap-2 rounded-xl bg-ember-500 px-6 py-4 text-lg font-bold text-white shadow-xl shadow-ember-700/30 transition-all hover:bg-ember-600 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-ember-400 focus-visible:ring-offset-2 focus-visible:ring-offset-bark-950 sm:min-h-[68px] sm:text-xl" data-id="element-19" data-mp="2y2kjt4pjbrel">
                    Yes, I pay more
                    <ArrowRightIcon className="h-5 w-5 transition-transform group-hover:translate-x-1" data-id="element-20" data-mp="3sts5rg2axhp6" />
                  </button>
                  <button type="button" onClick={() => setAnswer('no')} className="min-h-[60px] w-full touch-manipulation rounded-xl border border-white/25 bg-white/10 px-6 py-4 text-lg font-bold text-white backdrop-blur-sm transition-all hover:bg-white/20 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-bark-950 sm:min-h-[68px] sm:text-xl" data-id="element-21" data-mp="3osewew002wdi">
                    No, I pay less
                  </button>
                </div>

                <ul className="mt-7 space-y-2.5 sm:mt-9 sm:space-y-3" data-id="element-22" data-mp="1glbdswyocgcg">
                  {BENEFITS.map(benefit => <li key={benefit} className="flex items-start gap-2.5 text-sm font-medium text-white [text-shadow:0_1px_10px_rgba(8,6,4,0.9)] sm:text-base" data-id="element-23" data-mp="1kktybrml2per">
                      <CheckCircle2Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-ember-500" data-id="element-24" data-mp="1ui8err8ibe72" />
                      {benefit}
                    </li>)}
                </ul>
              </motion.section>}

            {answer === 'no' && <motion.section key="no" initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -12
          }} transition={{
            duration: 0.3,
            ease: 'easeOut'
          }} className="rounded-2xl border border-white/10 bg-bark-900/80 p-6 backdrop-blur sm:p-8" data-id="element-25" data-mp="16t5pbx7msh20">
                <h2 className="font-serif text-2xl font-bold leading-tight text-white sm:text-4xl" data-id="element-26" data-mp="b3estpbcztvk">
                  Bills go up every year — solar locks yours down.
                </h2>
                <p className="mt-4 font-serif text-lg leading-relaxed text-white/75" data-id="element-27" data-mp="kuj7sp11beqe">
                  Even at under $99 a month, most homeowners still qualify for a
                  free savings estimate. It's worth 60 seconds to find out.
                </p>
                <div className="mt-7 space-y-3" data-id="element-28" data-mp="3octa23yo4dfi">
                  <button type="button" onClick={goToOffer} className="group flex w-full items-center justify-center gap-2 rounded-xl bg-ember-500 px-6 py-5 text-lg font-bold text-white shadow-xl shadow-ember-700/30 transition-colors hover:bg-ember-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-ember-400 focus-visible:ring-offset-2 focus-visible:ring-offset-bark-900" data-id="element-29" data-mp="3kag5hivnqlkb">
                    Check my free estimate
                    <ArrowRightIcon className="h-5 w-5 transition-transform group-hover:translate-x-1" data-id="element-30" data-mp="2qgpezpxah9p9" />
                  </button>
                  <button type="button" onClick={() => setAnswer(null)} className="w-full px-6 py-2 text-sm font-medium text-white/50 transition-colors hover:text-white/80" data-id="element-31" data-mp="2veh6iiu0utsx">
                    Back to the question
                  </button>
                </div>
              </motion.section>}
          </AnimatePresence>
        </div>

        <footer className="mt-auto border-t border-white/10 pt-5" data-id="element-32" data-mp="30fwn9yp62e5i">
          <div className="flex flex-col gap-3 text-xs text-white/80 [text-shadow:0_1px_8px_rgba(8,6,4,0.9)] sm:flex-row sm:items-center sm:justify-between sm:text-sm" data-id="element-33" data-mp="30dk8fuepgjwo">
            <span className="inline-flex items-center gap-2" data-id="element-34" data-mp="3t3k73bim50r3">
              <ShieldCheckIcon className="h-4 w-4 text-ember-500" data-id="element-35" data-mp="1pehfaxdefeg" />
              Your information is secure
            </span>
            <span className="inline-flex items-center gap-2" data-id="element-36" data-mp="1xtui35wp2vmz">
              <UsersIcon className="h-4 w-4 text-ember-500" data-id="element-37" data-mp="1t2cj2ne8f54n" />
              Takes under 60 seconds · No obligation
            </span>
          </div>
          <p className="mt-4 text-[11px] leading-relaxed text-white/55 [text-shadow:0_1px_8px_rgba(8,6,4,0.9)]" data-id="element-38" data-mp="1o3reycvv01ax">
            Savings estimates vary by location, utility rates, roof condition
            and program eligibility. This site is an advertising page and is not
            affiliated with any government agency.
          </p>
        </footer>
      </div>

      <ExitIntentModal open={triggered} onClose={reset} onContinue={goToOffer} data-id="element-39" data-mp="26sy2cd7llh32" />
    </main>;
}